/**
 * Upload first, account after (2026-10-16).
 *
 * /start lets someone upload their bill before they have an account. The
 * server hands back signed upload URLs under pending/<id>/, we upload, and
 * keep the id in localStorage. Once they've verified their email, AppGate
 * calls claimPendingUpload and the server turns it into a new bill_drafts
 * row. Any bills they parked before stay as they are, and the claimed one
 * is their newest, so /case/draft with no id opens it.
 * See apps/server/src/routes/freeStart.ts.
 */

import { supabase } from './supabase';
import { API_BASE } from './apiConfig';

const KEY = 'ml_pending_upload';
// The phone number from /start (2026-11-02). Kept on the device until the
// upload is claimed, because a Google sign-in leaves the page and comes
// back, and the claim is where it gets saved on their account.
const PHONE_KEY = 'ml_pending_phone';

export function setPendingPhone(phone: string): void {
  try {
    localStorage.setItem(PHONE_KEY, phone);
  } catch {
    /* no localStorage, the bill page asks for it instead */
  }
}

function getPendingPhone(): string | null {
  try {
    return localStorage.getItem(PHONE_KEY);
  } catch {
    return null;
  }
}
// Same as the server's claim window.
const MAX_AGE_MS = 3 * 24 * 3600 * 1000;

export function getPendingUploadId(): string | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const { id, at } = JSON.parse(raw) as { id?: string; at?: number };
    if (!id || !at || Date.now() - at > MAX_AGE_MS) {
      localStorage.removeItem(KEY);
      return null;
    }
    return id;
  } catch {
    return null;
  }
}

function setPendingUploadId(id: string): void {
  try {
    localStorage.setItem(KEY, JSON.stringify({ id, at: Date.now() }));
  } catch {
    /* no localStorage, the claim just won't happen on this device */
  }
}

export function clearPendingUploadId(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* nothing to do */
  }
}

/**
 * Uploads the bill (first file) and any extra pages without an account.
 * Returns how many files made it, or throws with a message to show.
 */
export async function uploadWithoutAccount(
  files: File[],
  extras: { dueDate?: string | null } = {},
): Promise<{ id: string; uploaded: number }> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/free/start`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        files: files.map((f) => ({ name: f.name, size: f.size })),
        due_date: extras.dueDate || null,
      }),
    });
  } catch {
    throw new Error("We couldn't reach our server. Check your connection and try again.");
  }
  const body = (await res.json().catch(() => ({}))) as {
    id?: string;
    uploads?: { path: string; token: string }[];
    error?: string;
  };
  if (!res.ok || !body.id || !body.uploads) {
    throw new Error(body.error && body.error !== 'off' ? body.error : 'We could not start your upload. Try again in a moment.');
  }

  let uploaded = 0;
  for (let i = 0; i < body.uploads.length; i += 1) {
    const { path, token } = body.uploads[i];
    const { error } = await supabase.storage.from('medical_bills').uploadToSignedUrl(path, token, files[i]);
    if (!error) uploaded += 1;
    // The bill itself has to make it. A missing extra page is just dropped.
    else if (i === 0) throw new Error("Your bill didn't upload. Check your connection and try again.");
  }

  setPendingUploadId(body.id);
  // Tells Flavia a bill came in. Fire and forget.
  void fetch(`${API_BASE}/free/uploaded`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: body.id, files: uploaded }),
  }).catch(() => {});
  return { id: body.id, uploaded };
}

/** Writes the email they typed onto the upload, so we can see who got that far. */
export function notePendingEmail(id: string, email: string): void {
  void fetch(`${API_BASE}/free/pending-email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id, email }),
  }).catch(() => {});
}

/** After login: turns a /start upload on this device into a new waiting bill on the account. */
export async function claimPendingUpload(accessToken: string): Promise<boolean> {
  const id = getPendingUploadId();
  if (!id) return false;
  try {
    const res = await fetch(`${API_BASE}/free/claim`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
      body: JSON.stringify({ id, phone: getPendingPhone() }),
    });
    // Claimed, already claimed, or gone: either way don't try again.
    if (res.ok || res.status === 404 || res.status === 410) {
      clearPendingUploadId();
      try {
        localStorage.removeItem(PHONE_KEY);
      } catch {
        /* nothing to do */
      }
    }
    return res.ok;
  } catch {
    return false;
  }
}

/**
 * "No bill with you?" (2026-10-26). Saves the email (and the hospital and
 * due date if given) and the server sends the first reminder. Returns an
 * error message to show, or null when it worked.
 */
export async function requestBillReminder(input: {
  email: string;
  provider: string;
  dueDate: string;
}): Promise<string | null> {
  const utm: Record<string, string> = {};
  try {
    const params = new URLSearchParams(window.location.search);
    for (const [k, v] of params) if (k.startsWith('utm_')) utm[k] = v;
  } catch {
    /* no UTMs, fine */
  }
  try {
    const res = await fetch(`${API_BASE}/free/remind`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: input.email,
        provider: input.provider || null,
        dueDate: input.dueDate || null,
        page: document.referrer || null,
        utm: Object.keys(utm).length ? utm : null,
      }),
    });
    if (res.ok) return null;
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    return body.error ?? 'Something went wrong. Try again in a moment.';
  } catch {
    return "We couldn't reach the server. Check your connection and try again.";
  }
}

/**
 * The link in a reminder email carries ?rt=. Swap it for a session.
 * True when they're now signed in. Any failure just leaves them on /start.
 */
export async function signInWithReminderToken(token: string): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE}/free/remind-login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    });
    if (!res.ok) return false;
    const { hashed_token } = (await res.json()) as { hashed_token?: string };
    if (!hashed_token) return false;
    const { error } = await supabase.auth.verifyOtp({ token_hash: hashed_token, type: 'magiclink' });
    return !error;
  } catch {
    return false;
  }
}
